import { BLIP_STAGES, STAGE_LABELS, type BlipStage, type StageEvent } from '../../lib/blip';

export interface StageEntry {
  stage: BlipStage;
  startedAt: number;
  endedAt: number | null;
  /** Whatever that stage reported: the host it fetched, the characters it recovered. */
  detail: StageEvent;
}

type StageState = 'pending' | 'active' | 'done' | 'skipped';

// A stage the backend never announced is 'skipped', not 'pending' — pasting text
// legitimately has nothing to fetch, and leaving that step spinning forever would
// read as a hang.
function stateFor(stage: BlipStage, entries: StageEntry[], finished: boolean): StageState {
  const entry = entries.find((e) => e.stage === stage);
  if (entry) return entry.endedAt === null && !finished ? 'active' : 'done';

  const furthest = entries.length
    ? Math.max(...entries.map((e) => BLIP_STAGES.indexOf(e.stage)))
    : -1;
  if (finished || BLIP_STAGES.indexOf(stage) < furthest) return 'skipped';
  return 'pending';
}

function duration(entry: StageEntry | undefined): string | null {
  if (!entry || entry.endedAt === null) return null;
  const ms = entry.endedAt - entry.startedAt;
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

// What the backend actually reported for this stage, rather than the generic
// description — this is the part that proves the timeline is real.
function detailLine(entry: StageEntry | undefined, stage: BlipStage): string | null {
  if (!entry) return null;
  if (stage === 'fetching' && entry.detail.domain) return entry.detail.domain;
  if (stage === 'extracting' && typeof entry.detail.chars === 'number') {
    return `${entry.detail.chars.toLocaleString()} characters of prose`;
  }
  return null;
}

export function StageTimeline({
  entries,
  finished,
  failed,
}: {
  entries: StageEntry[];
  finished: boolean;
  failed: boolean;
}) {
  const active = entries.find((e) => e.endedAt === null && !finished);
  const activeIndex = active ? BLIP_STAGES.indexOf(active.stage) + 1 : entries.length;

  return (
    <div className="rounded-xl border border-edge bg-darker/80 p-6 sm:p-7">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="font-mono text-xs uppercase tracking-[0.24em] text-muted">
          Agent trace
        </h2>
        <span className="font-mono text-xs text-muted">
          {finished ? `${entries.length} steps` : `Step ${activeIndex} of ${BLIP_STAGES.length}`}
        </span>
      </div>

      {/* Screen readers get the stage change as text; the rail below is decorative
          duplication for sighted users. */}
      <p className="sr-only" role="status" aria-live="polite">
        {failed
          ? 'Briefing failed.'
          : finished
            ? 'Brief ready.'
            : active
              ? `Step ${activeIndex} of ${BLIP_STAGES.length}: ${STAGE_LABELS[active.stage].label}. ${STAGE_LABELS[active.stage].detail}.`
              : 'Starting.'}
      </p>

      <ol className="mt-6 space-y-0" aria-hidden="true">
        {BLIP_STAGES.map((stage, i) => {
          const entry = entries.find((e) => e.stage === stage);
          const state = stateFor(stage, entries, finished);
          const last = i === BLIP_STAGES.length - 1;
          const took = duration(entry);
          const reported = detailLine(entry, stage);

          return (
            <li key={stage} className="flex gap-4">
              <div className="flex flex-col items-center">
                <Dot state={state} />
                {!last && <Connector state={state} />}
              </div>

              <div className={`flex-1 ${last ? 'pb-0' : 'pb-6'}`}>
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                  <span
                    className={`font-mono text-sm uppercase tracking-[0.14em] transition-colors ${
                      state === 'active'
                        ? 'text-teal'
                        : state === 'done'
                          ? 'text-ink'
                          : 'text-muted'
                    }`}
                  >
                    {STAGE_LABELS[stage].label}
                  </span>
                  {took && <span className="font-mono text-[0.7rem] text-muted">{took}</span>}
                  {state === 'skipped' && (
                    <span className="font-mono text-[0.7rem] text-muted">not needed</span>
                  )}
                </div>
                {/* Hierarchy between reached and unreached steps comes from the
                    label colour and the dot, not from dimming this line below the
                    AA contrast floor. */}
                <p className="mt-1 text-sm leading-relaxed text-muted">
                  {STAGE_LABELS[stage].detail}
                </p>
                {reported && (
                  <p className="mt-1 truncate font-mono text-xs text-teal">{reported}</p>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function Dot({ state }: { state: StageState }) {
  if (state === 'done') {
    return (
      <span className="relative mt-1 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full bg-teal">
        <svg viewBox="0 0 10 10" className="h-2 w-2 text-darker" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M1.5 5.2 3.9 7.6 8.5 2.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (state === 'active') {
    return (
      <span className="relative mt-1 flex h-3.5 w-3.5 shrink-0 items-center justify-center">
        <span className="absolute inset-0 rounded-full bg-teal/40 motion-safe:animate-pulse-ring" />
        <span className="relative h-3.5 w-3.5 rounded-full bg-teal" />
      </span>
    );
  }
  return (
    <span
      className={`mt-1 h-3.5 w-3.5 shrink-0 rounded-full border ${
        state === 'skipped' ? 'border-dashed border-edge' : 'border-edge bg-panel'
      }`}
    />
  );
}

function Connector({ state }: { state: StageState }) {
  if (state === 'active') {
    return (
      <span className="relative my-1 w-px flex-1 overflow-hidden bg-edge">
        <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-transparent via-teal to-transparent motion-safe:animate-stage-sweep" />
      </span>
    );
  }
  return (
    <span className={`my-1 w-px flex-1 ${state === 'done' ? 'bg-teal/40' : 'bg-edge'}`} />
  );
}
